import { useState, useEffect, useCallback, useMemo } from 'react';
import { User } from 'firebase/auth';
import {
  onAuthChange,
  loginWithEmail,
  logout as firebaseLogout,
  checkUserRole,
} from '../services/firebase';
import type { AdminUser, BrandMembership } from '../types';

interface AuthState {
  user: AdminUser | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser: User | null) => {
      if (firebaseUser) {
        try {
          const { isAdmin, isContributor, brandMemberships } =
            await checkUserRole(firebaseUser.uid);
          setState({
            user: {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              isAdmin,
              isContributor,
              brandMemberships,
            },
            loading: false,
            error: null,
          });
        } catch (err) {
          setState({
            user: null,
            loading: false,
            error: 'Failed to verify admin status',
          });
        }
      } else {
        setState({
          user: null,
          loading: false,
          error: null,
        });
      }
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await loginWithEmail(email, password);
      // Auth state change will be handled by onAuthChange
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Login failed',
      }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      await firebaseLogout();
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Logout failed',
      }));
    }
  }, []);

  const isAdmin = state.user?.isAdmin ?? false;
  const brandMemberships: BrandMembership[] = useMemo(
    () => state.user?.brandMemberships ?? [],
    [state.user],
  );

  // Brand-scoped helpers. Super admins (isAdmin) implicitly pass for ALL brands;
  // server-side rules + Cloud Function checks remain the actual security
  // boundary (these are for UI gating only).
  const isBrandAdmin = useCallback(
    (brandId: string): boolean =>
      isAdmin ||
      brandMemberships.some(
        (m) => m.brandId === brandId && m.role === 'brandAdmin',
      ),
    [isAdmin, brandMemberships],
  );

  const isBrandMember = useCallback(
    (brandId: string): boolean =>
      isAdmin || brandMemberships.some((m) => m.brandId === brandId),
    [isAdmin, brandMemberships],
  );

  // Brands the user has an explicit membership on (admin or member role). Does
  // NOT enumerate all brands for super admins — there's no brand list here, and
  // super admins are routed to the admin home rather than a brand portal.
  const ownedBrandIds = useMemo(
    () => Array.from(new Set(brandMemberships.map((m) => m.brandId))),
    [brandMemberships],
  );

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.user,
    isAdmin,
    isContributor: state.user?.isContributor ?? false,
    brandMemberships,
    isBrandAdmin,
    isBrandMember,
    ownedBrandIds,
    login,
    logout,
  };
}
